// v40 — shared image helpers for client photo uploads.
//
// Fixes the "photos don't save" bug: the old admin + contractor pickers
// both had a hard-coded 1MB reject at the *browser* layer, so any real
// smartphone photo (typically 3–8MB) was silently blocked before it ever
// reached the backend — which itself accepts up to 10MB. This module:
//
//   • aligns the client-side cap with the backend (10MB)
//   • auto-downscales anything wider than 1600px OR heavier than 5MB
//     through a canvas re-encode so we stay well under the ingress body
//     limit AND MongoDB doesn't balloon
//   • always returns a clean `data:image/(jpeg|png|webp)` string ready
//     to POST to /api/clients/{cid}/photo

const MAX_BYTES = 10 * 1024 * 1024;         // 10 MB — matches backend
const DOWNSCALE_THRESHOLD_BYTES = 5 * 1024 * 1024;
const MAX_DIMENSION = 1600;                  // Longest side after downscale
const JPEG_QUALITY = 0.85;

const readAsDataURL = (file) =>
  new Promise((res, rej) => {
    const r = new FileReader();
    r.onload = () => res(r.result);
    r.onerror = () => rej(new Error("Could not read image"));
    r.readAsDataURL(file);
  });

const loadImage = (dataUrl) =>
  new Promise((res, rej) => {
    const img = new Image();
    img.onload = () => res(img);
    img.onerror = () => rej(new Error("Could not decode image"));
    img.src = dataUrl;
  });

const downscaleDataUrl = async (dataUrl) => {
  const img = await loadImage(dataUrl);
  const { width, height } = img;
  const longest = Math.max(width, height);
  const scale = longest > MAX_DIMENSION ? MAX_DIMENSION / longest : 1;
  const w = Math.round(width * scale);
  const h = Math.round(height * scale);
  const canvas = document.createElement("canvas");
  canvas.width = w;
  canvas.height = h;
  const ctx = canvas.getContext("2d");
  ctx.drawImage(img, 0, 0, w, h);
  // Re-encode as JPEG — smaller, universally supported. PNGs with alpha
  // will lose transparency but that's fine for profile photos.
  return canvas.toDataURL("image/jpeg", JPEG_QUALITY);
};

/**
 * Read + normalize a File (from an <input type=file> onChange event)
 * into a backend-ready `data:image/...` URL. Enforces the 10MB cap and
 * silently downscales anything larger than ~5MB so uploads succeed on
 * typical mobile photo sizes without any user friction.
 *
 * Throws with a user-facing message on invalid or oversize files.
 */
export async function prepareClientPhoto(file) {
  if (!file) throw new Error("No file selected");
  if (!file.type || !file.type.startsWith("image/")) {
    throw new Error("Please pick an image file (PNG, JPEG, or WebP).");
  }
  if (file.size > MAX_BYTES) {
    throw new Error("Image too large (max 10MB). Try a smaller photo.");
  }
  let dataUrl = await readAsDataURL(file);
  const needsDownscale =
    file.size > DOWNSCALE_THRESHOLD_BYTES || file.type === "image/heic" || file.type === "image/heif";
  if (needsDownscale) {
    try {
      dataUrl = await downscaleDataUrl(dataUrl);
    } catch (e) {
      // If canvas decoding fails (e.g. HEIC on browsers without native support),
      // fall through with the original data URL — the backend may still accept
      // it if the raw bytes are under the 10MB cap.
      console.warn("Photo downscale failed, sending original:", e);
    }
  }
  return dataUrl;
}
